import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { AffectedItemsComponent } from '../affected-items/affected-items.component';
import { ChangeWorkflowComponent, ChangeWorkflowStepEvent } from '../change-workflow/change-workflow.component';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import {
  Change,
  ChangeType,
  ChangeState,
  ChangePriority,
  ChangeRequestType,
  ChangeTransitionResult,
  WorkflowProgress,
  WorkflowState,
  WorkflowStep
} from '../../models/file-vault.models';

export interface ChangeDetailEvent {
  action: 'submitted' | 'approved' | 'rejected' | 'closed' | 'cancelled' | 'reopened' | 'deleted' | 'converted' | 'auto-converted';
  change: Change;
  result?: ChangeTransitionResult;
  /** When an ECR is auto-converted to ECO on approval, this holds the new ECO ID */
  autoConvertedEcoId?: string;
  /** When an ECR is auto-converted to ECO on approval, this holds the new ECO number */
  autoConvertedEcoNumber?: string;
}

@Component({
  selector: 'fv-change-detail',
  standalone: true,
  template: `
    <div class="change-detail-container" *ngIf="change">
      <!-- Header -->
      <div class="header">
        <div class="header-left">
          <span class="change-number">{{ change.changeNumber }}</span>
          <span class="type-badge" [ngClass]="getTypeBadgeClass(change.changeType)">
            {{ getTypeLabel(change.changeType) }}
          </span>
          <span class="state-badge" [ngClass]="getStateBadgeClass(change.state)">
            {{ getStateLabel(change.state) }}
          </span>
          <span class="priority-badge" [ngClass]="getPriorityClass(change.priority)">
            {{ getPriorityLabel(change.priority) }}
          </span>
        </div>
        <div class="header-right">
          <ng-container *ngFor="let action of allowedActions">
            <button kendoButton
                    [look]="getActionLook(action)"
                    [themeColor]="getActionTheme(action)"
                    (click)="openActionDialog(action)"
                    [disabled]="isProcessing">
              {{ getActionLabel(action) }}
            </button>
          </ng-container>
        </div>
      </div>

      <!-- Title & Description -->
      <div class="section" *ngIf="!canEdit">
        <h2 class="title">{{ change.title }}</h2>
        <p class="description" *ngIf="change.description">{{ change.description }}</p>
      </div>

      <div class="section editable-section" *ngIf="canEdit">
        <div class="input-section">
          <label>Title</label>
          <input kendoTextBox [(ngModel)]="change.title" (blur)="onFieldBlur()" />
        </div>

        <!-- Request Type — only shown on ECRs (ECOs inherit type from parent ECR) -->
        <div class="input-section" *ngIf="change.changeType === ChangeType.ECR">
          <label>Request Type <span class="required">*</span></label>
          <kendo-dropdownlist
            [(ngModel)]="change.requestType"
            [data]="requestTypeOptions"
            textField="label"
            valueField="value"
            [valuePrimitive]="true"
            (valueChange)="onRequestTypeChange($event)">
          </kendo-dropdownlist>
          <small class="field-hint" *ngIf="change.requestType === ChangeRequestType.NewCreation">
            No affected items are required — engineering will add them when the ECO is created.
          </small>
        </div>

        <div class="input-section">
          <label>Description <span class="required">*</span></label>
          <textarea kendoTextArea
                    [(ngModel)]="change.description"
                    [rows]="3"
                    placeholder="Provide a description (required for submission)"
                    (blur)="onFieldBlur()">
          </textarea>
        </div>
        <div class="input-section">
          <label>Justification</label>
          <textarea kendoTextArea
                    [(ngModel)]="change.justification"
                    [rows]="3"
                    placeholder="Optional justification"
                    (blur)="onFieldBlur()">
          </textarea>
        </div>
        <div class="save-status" *ngIf="saveStatus">{{ saveStatus }}</div>
      </div>

      <!-- Details Grid -->
      <div class="details-grid">
        <div class="detail-item">
          <label>Raised By</label>
          <span>{{ change.raisedBy }}</span>
        </div>
        <div class="detail-item">
          <label>Raised At</label>
          <span>{{ change.raisedAt | date:'medium' }}</span>
        </div>
        <div class="detail-item" *ngIf="change.assigneeName">
          <label>Assigned To</label>
          <span>{{ change.assigneeName }}</span>
        </div>
        <div class="detail-item" *ngIf="change.dueDate">
          <label>Due Date</label>
          <span [ngClass]="{ 'overdue': isOverdue() }">{{ change.dueDate | date:'mediumDate' }}</span>
        </div>
        <div class="detail-item" *ngIf="change.effectiveDate">
          <label>Effective Date</label>
          <span>{{ change.effectiveDate | date:'mediumDate' }}</span>
        </div>
        <div class="detail-item" *ngIf="change.approvedAt">
          <label>Approved</label>
          <span>{{ change.approvedAt | date:'medium' }} by {{ change.approvedBy }}</span>
        </div>
        <div class="detail-item" *ngIf="change.closedAt">
          <label>Closed</label>
          <span>{{ change.closedAt | date:'medium' }} by {{ change.closedBy }}</span>
        </div>
        <div class="detail-item" *ngIf="change.parentChangeNumber">
          <label>Parent Change</label>
          <a class="parent-link" (click)="onParentClick()">{{ change.parentChangeNumber }}</a>
        </div>
      </div>

      <!-- Workflow Sign-offs -->
      <div class="section">
        <fv-change-workflow
          [changeId]="changeId"
          [currentLoginId]="currentLoginId"
          (stepCompleted)="onWorkflowStepCompleted($event)"
          (workflowCompleted)="onWorkflowCompleted()">
        </fv-change-workflow>
      </div>

      <!-- Justification -->
      <div class="section" *ngIf="change.justification && !canEdit">
        <h3>Justification</h3>
        <p class="justification">{{ change.justification }}</p>
      </div>

      <!-- Affected Items -->
      <div class="section">
        <!-- New Creation ECRs: no affected items needed at this stage -->
        <div class="new-creation-notice"
             *ngIf="change.changeType === ChangeType.ECR && change.requestType === ChangeRequestType.NewCreation">
          <div class="notice-icon">ℹ️</div>
          <div class="notice-body">
            <strong>New Creation Request</strong>
            <p>
              Affected items are not required for a New Creation ECR.
              Engineering will add the new part or document as an affected item
              when the resulting ECO is created.
            </p>
          </div>
        </div>
        <fv-affected-items
          *ngIf="change.changeType !== ChangeType.ECR || change.requestType !== ChangeRequestType.NewCreation"
          [changeId]="changeId"
          [changeState]="change.state">
        </fv-affected-items>
      </div>

      <!-- Action Dialog -->
      <kendo-dialog *ngIf="showActionDialog"
                    [title]="actionDialogTitle"
                    [width]="450"
                    (close)="closeActionDialog()">
        <div class="dialog-content">
          <p class="action-description">{{ actionDialogDescription }}</p>

          <!-- Notes/Reason input -->
          <div class="input-section" *ngIf="actionRequiresInput">
            <label>{{ actionInputLabel }} <span class="required" *ngIf="actionInputRequired">*</span></label>
            <textarea kendoTextArea
                      [(ngModel)]="actionInput"
                      [rows]="3"
                      [placeholder]="actionInputPlaceholder">
            </textarea>
          </div>

          <div class="input-section" *ngIf="currentAction === 'start-implementation'">
            <label>Required Sign-offs</label>
            <div class="checkbox-grid">
              <label class="checkbox-item" *ngFor="let option of ecoSignoffOptions">
                <input type="checkbox" [(ngModel)]="ecoSignoffs[option.key]" />
                {{ option.label }}
              </label>
            </div>
            <label class="toggle">
              <input type="checkbox" [(ngModel)]="ecoSignoffsParallel" />
              Allow sign-offs in parallel
            </label>
          </div>

          <!-- Warning -->
          <div class="warning" *ngIf="showActionWarning">
            <span class="warning-icon">{{ actionWarningIcon }}</span>
            <span>{{ actionWarningText }}</span>
          </div>

          <!-- Validation hint for submit when description is missing -->
          <div class="validation-hint" *ngIf="currentAction === 'submit' && !change?.description?.trim()">
            A description is required before submitting. Please close this dialog and fill in the description field above.
          </div>

          <!-- Action error -->
          <div class="action-error" *ngIf="actionError">{{ actionError }}</div>
        </div>

        <kendo-dialog-actions>
          <button kendoButton (click)="closeActionDialog()">Cancel</button>
          <button kendoButton
                  [themeColor]="getActionTheme(currentAction!)"
                  (click)="executeAction()"
                  [disabled]="isProcessing || (actionInputRequired && !actionInput?.trim()) || (currentAction === 'submit' && !change?.description?.trim())">
            {{ isProcessing ? 'Processing...' : 'Confirm' }}
          </button>
        </kendo-dialog-actions>
      </kendo-dialog>

    </div>

    <!-- Loading State -->
    <div class="loading" *ngIf="isLoading">Loading change details...</div>
  `,
  styles: [`
    .change-detail-container {
      padding: 16px;
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
      flex-wrap: wrap;
      gap: 12px;
    }

    .header-left {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .header-right {
      display: flex;
      gap: 8px;
    }

    .change-number {
      font-family: monospace;
      font-size: 18px;
      font-weight: 600;
    }

    .type-badge {
      display: inline-block;
      padding: 4px 10px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
    }

    .type-badge.ecr { background: var(--fv-status-info-bg); color: var(--fv-status-info-fg); }
    .type-badge.eco { background: var(--fv-status-warning-bg); color: var(--fv-status-warning-fg); }
    .type-badge.ecn { background: var(--fv-status-purple-bg); color: var(--fv-status-purple-fg); }
    .type-badge.deviation { background: var(--fv-status-error-bg); color: var(--fv-status-error-fg); }

    .state-badge {
      display: inline-block;
      padding: 4px 12px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 500;
    }

    .state-badge.draft { background: var(--fv-status-draft-bg); color: var(--fv-status-draft-fg); }
    .state-badge.submitted { background: var(--fv-status-info-bg); color: var(--fv-status-info-fg); }
    .state-badge.in-review { background: var(--fv-status-warning-bg); color: var(--fv-status-warning-fg); }
    .state-badge.approved { background: var(--fv-status-success-bg); color: var(--fv-status-success-fg); }
    .state-badge.rejected { background: var(--fv-status-error-bg); color: var(--fv-status-error-fg); }
    .state-badge.in-implementation { background: var(--fv-status-indigo-bg); color: var(--fv-status-indigo-fg); }
    .state-badge.implemented { background: var(--fv-status-teal-bg); color: var(--fv-status-teal-fg); }
    .state-badge.closed { background: var(--fv-status-bluegrey-bg); color: var(--fv-status-bluegrey-fg); }
    .state-badge.cancelled { background: var(--fv-surface-alt); color: var(--fv-text-muted); }

    .priority-badge {
      font-size: 11px;
      font-weight: 500;
      padding: 2px 8px;
      border-radius: 4px;
    }

    .priority-badge.low { background: var(--fv-status-success-bg); color: var(--fv-status-success-fg); }
    .priority-badge.medium { background: var(--fv-status-warning-bg); color: var(--fv-status-warning-fg); }
    .priority-badge.high { background: var(--fv-status-error-bg); color: var(--fv-status-error-fg); }
    .priority-badge.critical { background: #b71c1c; color: white; }

    .section {
      margin-bottom: 24px;
    }

    .section h3 {
      margin: 0 0 8px 0;
      font-size: 14px;
      font-weight: 600;
      color: var(--fv-text);
    }

    .title {
      margin: 0 0 8px 0;
      font-size: 20px;
      font-weight: 500;
    }

    .description, .justification {
      color: var(--fv-text-secondary);
      line-height: 1.5;
      white-space: pre-wrap;
    }

    .field-hint {
      display: block;
      margin-top: 4px;
      font-size: 12px;
      color: var(--fv-text-secondary);
    }

    .new-creation-notice {
      display: flex;
      gap: 12px;
      align-items: flex-start;
      padding: 12px 16px;
      background: var(--fv-surface-alt);
      border: 1px solid var(--fv-border);
      border-left: 4px solid #0d6efd;
      border-radius: 6px;
      margin-bottom: 12px;
    }

    .new-creation-notice .notice-icon {
      font-size: 20px;
      line-height: 1;
    }

    .new-creation-notice .notice-body strong {
      display: block;
      margin-bottom: 4px;
      color: var(--fv-text);
    }

    .new-creation-notice .notice-body p {
      margin: 0;
      font-size: 13px;
      color: var(--fv-text-secondary);
    }

    .details-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 16px;
      margin-bottom: 24px;
      padding: 16px;
      background: var(--fv-surface-alt);
      border-radius: 8px;
    }

    .workflow-card {
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 12px;
      border: 1px solid var(--fv-border);
      border-radius: 8px;
      background: var(--fv-surface);
      font-size: 13px;
      color: var(--fv-text);
    }

    .workflow-line label {
      display: block;
      font-size: 11px;
      color: var(--fv-text-muted);
      text-transform: uppercase;
      margin-bottom: 2px;
    }

    .workflow-error {
      color: var(--fv-status-error-fg);
      font-size: 12px;
    }

    .workflow-hint {
      color: var(--fv-text-muted);
      font-size: 12px;
    }

    .detail-item label {
      display: block;
      font-size: 11px;
      color: var(--fv-text-muted);
      text-transform: uppercase;
      margin-bottom: 4px;
    }

    .detail-item span {
      font-size: 14px;
    }

    .overdue {
      color: var(--fv-status-error-fg);
      font-weight: 500;
    }

    .parent-link {
      color: var(--fv-text-accent);
      cursor: pointer;
    }

    .parent-link:hover {
      text-decoration: underline;
    }

    .dialog-content {
      padding: 16px 0;
    }

    .action-description {
      margin: 0 0 16px 0;
      color: var(--fv-text-secondary);
    }

    .input-section {
      margin-top: 16px;
    }

    .input-section label {
      display: block;
      margin-bottom: 6px;
      font-weight: 500;
      font-size: 13px;
    }

    .required {
      color: var(--fv-status-error-fg);
    }

    .input-section textarea {
      width: 100%;
    }

    .checkbox-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
      gap: 8px 12px;
      margin-top: 8px;
    }

    .checkbox-item {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 13px;
      color: var(--fv-text);
    }

    .warning {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      padding: 12px;
      background: var(--fv-status-warning-bg);
      border-radius: 4px;
      margin-top: 16px;
      font-size: 13px;
      color: var(--fv-status-warning-fg);
    }

    .editable-section {
      padding: 16px;
      background: var(--fv-surface-alt);
      border-radius: 8px;
      border: 1px dashed var(--fv-border);
    }

    .editable-section .input-section {
      margin-top: 0;
      margin-bottom: 12px;
    }

    .editable-section .input-section:last-of-type {
      margin-bottom: 0;
    }

    .editable-section input[kendoTextBox] {
      width: 100%;
    }

    .save-status {
      font-size: 12px;
      color: var(--fv-text-muted);
      margin-top: 8px;
      font-style: italic;
    }

    .save-status.error {
      color: var(--fv-status-error-fg);
    }

    .validation-hint {
      padding: 10px 12px;
      background: var(--fv-status-warning-bg);
      border-radius: 4px;
      margin-top: 12px;
      font-size: 13px;
      color: var(--fv-status-warning-fg);
    }

    .action-error {
      padding: 10px 12px;
      background: var(--fv-status-error-bg);
      border-radius: 4px;
      margin-top: 12px;
      font-size: 13px;
      color: var(--fv-status-error-fg);
    }

    .loading {
      padding: 48px;
      text-align: center;
      color: var(--fv-text-secondary);
    }
  `],
  imports: [CommonModule, FormsModule, ButtonsModule, DialogModule, InputsModule, DropDownsModule, AffectedItemsComponent, ChangeWorkflowComponent]
})
export class ChangeDetailComponent implements OnInit, OnChanges {
  private api = inject(FileVaultApiService);

  @ViewChild(ChangeWorkflowComponent) private workflowComponent?: ChangeWorkflowComponent;

  // Expose enums to the template
  readonly ChangeType = ChangeType;
  readonly ChangeRequestType = ChangeRequestType;

  /** Request type options for the dropdown */
  readonly requestTypeOptions = [
    { value: ChangeRequestType.Modification, label: 'Modification / Improvement' },
    { value: ChangeRequestType.NewCreation,  label: 'New Part / Document Creation' },
    { value: ChangeRequestType.Obsolescence, label: 'Obsolescence / Retirement' }
  ];

  /** Change ID to display */
  @Input() changeId!: string;

  /** Preloaded change object (optional) */
  @Input() change?: Change;

  /** Optional current LoginId for workflow actions */
  @Input() currentLoginId?: string;

  /** Emitted when a state transition occurs */
  @Output() stateChanged = new EventEmitter<ChangeDetailEvent>();
  /** Emitted when parent change is clicked */
  @Output() parentClick = new EventEmitter<string>();

  allowedTransitions: ChangeState[] = [];
  isLoading = false;
  isProcessing = false;
  workflowProgress?: WorkflowProgress;
  currentWorkflowStep?: WorkflowStep;
  isWorkflowLoading = false;
  workflowActionError = '';

  // Action dialog state
  showActionDialog = false;
  currentAction?: string;
  actionInput = '';
  ecoSignoffOptions = [
    { key: 'Accounting', label: 'Accounting' },
    { key: 'Procurement', label: 'Procurement' },
    { key: 'Quality', label: 'Quality' },
    { key: 'Software Support', label: 'Software Support' }
  ];
  ecoSignoffs: Record<string, boolean> = {};
  ecoSignoffsParallel = true;
  actionError = '';

  // Inline edit state
  saveStatus = '';
  private saveTimeout: any;


  get canEdit(): boolean {
    return this.change?.state === ChangeState.Draft || this.change?.state === ChangeState.Rejected;
  }

  get allowedActions(): string[] {
    if (!this.change) return [];

    const actions: string[] = [];
    const state = this.change.state;

    if (state === ChangeState.Draft) {
      if (this.change.changeType === ChangeType.ECR) {
        actions.push('submit');
      } else if (this.change.changeType === ChangeType.ECO) {
        actions.push('start-implementation');
      }
      actions.push('delete');
    }
    if (state === ChangeState.Submitted || state === ChangeState.InReview) {
      actions.push('approve', 'reject');
    }
    if (state === ChangeState.Approved) {
      if (this.change.changeType === ChangeType.ECR) {
        actions.push('convert');
      }
      if (this.change.changeType === ChangeType.ECO) {
        actions.push('start-implementation');
      }
    }
    if (state === ChangeState.InImplementation) {
      actions.push('implement');
    }
    if (state === ChangeState.Implemented) {
      actions.push('close');
    }
    if (state === ChangeState.Rejected) {
      actions.push('reopen');
    }
    if (state !== ChangeState.Closed && state !== ChangeState.Cancelled) {
      actions.push('cancel');
    }

    return actions;
  }

  get actionDialogTitle(): string {
    switch (this.currentAction) {
      case 'submit': return 'Submit Change';
      case 'approve': return 'Approve Change';
      case 'reject': return 'Reject Change';
      case 'start-implementation': return 'Start Implementation';
      case 'implement': return 'Mark as Implemented';
      case 'close': return 'Close Change';
      case 'cancel': return 'Cancel Change';
      case 'reopen': return 'Reopen Change';
      case 'convert': return 'Convert to ECO';
      case 'delete': return 'Delete Change';
      default: return 'Confirm Action';
    }
  }

  get actionDialogDescription(): string {
    switch (this.currentAction) {
      case 'submit': return 'Submit this change for review and approval.';
      case 'approve': return 'Approve this change to proceed with implementation.';
      case 'reject': return 'Reject this change and return it to the requester.';
      case 'start-implementation': return 'Begin implementation of this approved change.';
      case 'implement': return 'Mark this change as fully implemented.';
      case 'close': return 'Close this change after successful implementation.';
      case 'cancel': return 'Cancel this change. This action cannot be undone.';
      case 'reopen': return 'Reopen this rejected change for further editing.';
      case 'convert': return 'Convert this ECR to an ECO for formal implementation.';
      case 'delete': return 'Permanently delete this draft change.';
      default: return '';
    }
  }

  get actionRequiresInput(): boolean {
    return ['submit', 'approve', 'reject', 'implement', 'close', 'cancel', 'convert'].includes(this.currentAction || '');
  }

  get actionInputRequired(): boolean {
    return ['reject', 'cancel'].includes(this.currentAction || '');
  }

  get actionInputLabel(): string {
    if (this.currentAction === 'reject' || this.currentAction === 'cancel') {
      return 'Reason';
    }
    return 'Notes';
  }

  get actionInputPlaceholder(): string {
    if (this.actionInputRequired) {
      return 'Please provide a reason...';
    }
    return 'Optional notes...';
  }

  get showActionWarning(): boolean {
    return ['cancel', 'delete', 'reject'].includes(this.currentAction || '');
  }

  get actionWarningIcon(): string {
    return this.currentAction === 'delete' ? '⛔' : '⚠️';
  }

  get actionWarningText(): string {
    switch (this.currentAction) {
      case 'cancel': return 'This will permanently cancel the change.';
      case 'delete': return 'This will permanently delete the change and cannot be undone.';
      case 'reject': return 'This will return the change to the requester for revision.';
      default: return '';
    }
  }

  ngOnInit(): void {
    if (!this.change && this.changeId) {
      this.loadChange();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['changeId'] && !changes['changeId'].firstChange) {
      this.loadChange();
    }
  }

  async loadChange(): Promise<void> {
    if (!this.changeId) return;

    this.isLoading = true;
    try {
      this.change = await this.api.getChange(this.changeId, true).toPromise();
      await this.loadWorkflowStatus();
    } catch (error) {
      console.error('Failed to load change', error);
    } finally {
      this.isLoading = false;
    }
  }

  openActionDialog(action: string): void {
    this.currentAction = action;
    this.actionInput = '';
    this.actionError = '';
    if (action === 'start-implementation') {
      this.ecoSignoffs = this.ecoSignoffOptions.reduce((acc, option) => {
        acc[option.key] = false;
        return acc;
      }, {} as Record<string, boolean>);
      this.ecoSignoffsParallel = true;
    }
    this.showActionDialog = true;
  }

  closeActionDialog(): void {
    this.showActionDialog = false;
    this.currentAction = undefined;
    this.actionInput = '';
    this.actionError = '';
  }

  /** Auto-save title/description/justification when user tabs out of a field */
  async onFieldBlur(): Promise<void> {
    if (!this.change || !this.changeId) return;

    clearTimeout(this.saveTimeout);
    this.saveTimeout = setTimeout(async () => {
      this.saveStatus = 'Saving...';
      try {
        await this.api.updateChange(this.changeId, {
          title: this.change!.title,
          description: this.change!.description,
          justification: this.change!.justification,
          requestType: this.change!.requestType
        }).toPromise();
        this.saveStatus = 'Saved';
        setTimeout(() => { this.saveStatus = ''; }, 2000);
      } catch (error) {
        console.error('Failed to save change', error);
        this.saveStatus = 'Failed to save';
      }
    }, 300);
  }

  /** Immediately persist the request type change (no blur delay needed). */
  async onRequestTypeChange(value: ChangeRequestType): Promise<void> {
    if (!this.change || !this.changeId) return;
    this.change.requestType = value;
    try {
      await this.api.updateChange(this.changeId, { requestType: value }).toPromise();
    } catch (error) {
      console.error('Failed to save request type', error);
    }
  }

  private getSelectedEcoSignoffs(): string[] {
    return this.ecoSignoffOptions
      .map(option => option.key)
      .filter(key => this.ecoSignoffs[key]);
  }

  async executeAction(): Promise<void> {
    if (!this.change || !this.currentAction) return;

    this.isProcessing = true;
    this.actionError = '';
    try {
      // If submitting, auto-save any pending edits first
      if (this.currentAction === 'submit' && this.canEdit) {
        await this.api.updateChange(this.change.id, {
          title: this.change.title,
          description: this.change.description,
          justification: this.change.justification,
          requestType: this.change.requestType
        }).toPromise();
      }

      let result: ChangeTransitionResult | undefined;
      let eventAction: ChangeDetailEvent['action'] | undefined;

      switch (this.currentAction) {
        case 'submit':
          result = await this.api.submitChange(this.change.id, { notes: this.actionInput || undefined }).toPromise();
          eventAction = 'submitted';
          break;
        case 'approve':
          result = await this.api.approveChange(this.change.id, { comments: this.actionInput || undefined }).toPromise();
          eventAction = 'approved';
          break;
        case 'reject':
          result = await this.api.rejectChange(this.change.id, { reason: this.actionInput }).toPromise();
          eventAction = 'rejected';
          break;
        case 'start-implementation':
          result = await this.api.startImplementation(this.change.id, {
            notes: this.actionInput || undefined,
            requiredSignoffs: this.getSelectedEcoSignoffs(),
            signoffsParallel: this.ecoSignoffsParallel
          }).toPromise();
          break;
        case 'implement':
          result = await this.api.implementChange(this.change.id, { notes: this.actionInput || undefined }).toPromise();
          break;
        case 'close':
          result = await this.api.closeChange(this.change.id, { notes: this.actionInput || undefined }).toPromise();
          eventAction = 'closed';
          break;
        case 'cancel':
          result = await this.api.cancelChange(this.change.id, this.actionInput).toPromise();
          eventAction = 'cancelled';
          break;
        case 'reopen':
          result = await this.api.reopenChange(this.change.id).toPromise();
          eventAction = 'reopened';
          break;
        case 'convert':
          const eco = await this.api.convertToEco(this.change.id, { notes: this.actionInput || undefined }).toPromise();
          if (eco) {
            this.stateChanged.emit({ action: 'converted', change: eco });
          }
          this.closeActionDialog();
          return;
        case 'delete':
          await this.api.deleteChange(this.change.id).toPromise();
          this.stateChanged.emit({ action: 'deleted', change: this.change });
          this.closeActionDialog();
          return;
      }

      if (result?.success) {
        // Check if an ECR was auto-converted to ECO on approval
        const returnedChange = result.change || this.change;
        const autoEcoId = returnedChange?.attributes?.['AutoConvertedEcoId'];
        const autoEcoNumber = returnedChange?.attributes?.['AutoConvertedEcoNumber'];

        if (eventAction === 'approved' && autoEcoId) {
          this.stateChanged.emit({
            action: 'auto-converted',
            change: returnedChange!,
            result,
            autoConvertedEcoId: autoEcoId,
            autoConvertedEcoNumber: autoEcoNumber
          });
        } else if (eventAction) {
          this.stateChanged.emit({ action: eventAction, change: returnedChange!, result });
        }
        await this.loadChange();
        this.workflowComponent?.reload();
        this.closeActionDialog();
      } else if (result) {
        this.actionError = result.errorMessage || 'Action failed. Please try again.';
      } else {
        this.closeActionDialog();
      }
    } catch (error: any) {
      console.error('Action failed', error);
      this.actionError = error?.error?.message || error?.message || 'An unexpected error occurred. Please try again.';
    } finally {
      this.isProcessing = false;
    }
  }

  async loadWorkflowStatus(): Promise<void> {
    if (!this.change) return;

    this.isWorkflowLoading = true;
    try {
      let instanceId = this.change.workflowInstanceId;
      if (!instanceId) {
        const instance = await this.api.getWorkflowForEntity('Change', this.change.id).toPromise();
        instanceId = instance?.id;
      }

      if (!instanceId) {
        this.workflowProgress = undefined;
        this.currentWorkflowStep = undefined;
        return;
      }

      this.workflowProgress = await this.api.getWorkflowProgress(instanceId).toPromise();
      this.currentWorkflowStep = await this.api.getCurrentStep(instanceId).toPromise();
    } catch (error) {
      console.error('Failed to load workflow status', error);
      this.workflowProgress = undefined;
      this.currentWorkflowStep = undefined;
    } finally {
      this.isWorkflowLoading = false;
    }
  }

  get canSignIn(): boolean {
    if (!this.change || this.change.changeType !== ChangeType.ECO) return false;
    if (!this.workflowProgress || this.workflowProgress.state !== WorkflowState.InProgress) return false;
    if (!this.currentWorkflowStep) return false;
    if (this.currentWorkflowStep.assignedTo) return false;
    return true;
  }

  async signInToWorkflow(): Promise<void> {
    if (!this.currentWorkflowStep) return;
    if (!this.currentLoginId) {
      this.workflowActionError = 'LoginId is required to sign in.';
      return;
    }

    this.isProcessing = true;
    this.workflowActionError = '';
    try {
      await this.api.claimStep(this.currentWorkflowStep.id, {}, this.currentLoginId).toPromise();
      await this.loadWorkflowStatus();
    } catch (error) {
      console.error('Failed to sign in to workflow', error);
      this.workflowActionError = 'Failed to sign in to the workflow step.';
    } finally {
      this.isProcessing = false;
    }
  }

  /** Handle sign-off step completed from the embedded workflow component */
  onWorkflowStepCompleted(event: ChangeWorkflowStepEvent): void {
    // Reload the change to pick up any state transitions triggered by the workflow
    this.loadChange();
  }

  /** Handle entire workflow completed */
  onWorkflowCompleted(): void {
    this.loadChange();
  }


  onParentClick(): void {
    if (this.change?.parentChangeId) {
      this.parentClick.emit(this.change.parentChangeId);
    }
  }

  isOverdue(): boolean {
    if (!this.change?.dueDate) return false;
    const dueDate = new Date(this.change.dueDate);
    const now = new Date();
    return dueDate < now &&
           this.change.state !== ChangeState.Closed &&
           this.change.state !== ChangeState.Cancelled;
  }

  refresh(): void {
    this.loadChange();
  }

  getActionLook(action: string): 'flat' | 'outline' {
    if (['cancel', 'delete', 'reject'].includes(action)) {
      return 'outline';
    }
    return 'flat';
  }

  getActionTheme(action: string): 'primary' | 'warning' | 'error' | 'base' {
    switch (action) {
      case 'submit':
      case 'approve':
      case 'close':
        return 'primary';
      case 'reject':
      case 'cancel':
      case 'delete':
        return 'error';
      default:
        return 'base';
    }
  }

  getActionLabel(action: string): string {
    switch (action) {
      case 'submit': return 'Submit';
      case 'approve': return 'Approve';
      case 'reject': return 'Reject';
      case 'start-implementation': return 'Start Implementation';
      case 'implement': return 'Implement';
      case 'close': return 'Close';
      case 'cancel': return 'Cancel';
      case 'reopen': return 'Reopen';
      case 'convert': return 'Convert to ECO';
      case 'delete': return 'Delete';
      default: return action;
    }
  }

  getTypeLabel(type: ChangeType): string {
    switch (type) {
      case ChangeType.ECR: return 'ECR';
      case ChangeType.ECO: return 'ECO';
      case ChangeType.ECN: return 'ECN';
      case ChangeType.Deviation: return 'Deviation';
      default: return 'Unknown';
    }
  }

  getTypeBadgeClass(type: ChangeType): string {
    switch (type) {
      case ChangeType.ECR: return 'ecr';
      case ChangeType.ECO: return 'eco';
      case ChangeType.ECN: return 'ecn';
      case ChangeType.Deviation: return 'deviation';
      default: return '';
    }
  }

  getStateLabel(state: ChangeState): string {
    switch (state) {
      case ChangeState.Draft: return 'Draft';
      case ChangeState.Submitted: return 'Submitted';
      case ChangeState.InReview: return 'In Review';
      case ChangeState.Approved: return 'Approved';
      case ChangeState.Rejected: return 'Rejected';
      case ChangeState.InImplementation: return 'Implementing';
      case ChangeState.Implemented: return 'Implemented';
      case ChangeState.Closed: return 'Closed';
      case ChangeState.Cancelled: return 'Cancelled';
      default: return 'Unknown';
    }
  }

  getStateBadgeClass(state: ChangeState): string {
    switch (state) {
      case ChangeState.Draft: return 'draft';
      case ChangeState.Submitted: return 'submitted';
      case ChangeState.InReview: return 'in-review';
      case ChangeState.Approved: return 'approved';
      case ChangeState.Rejected: return 'rejected';
      case ChangeState.InImplementation: return 'in-implementation';
      case ChangeState.Implemented: return 'implemented';
      case ChangeState.Closed: return 'closed';
      case ChangeState.Cancelled: return 'cancelled';
      default: return '';
    }
  }

  getPriorityLabel(priority: ChangePriority): string {
    switch (priority) {
      case ChangePriority.Low: return 'Low';
      case ChangePriority.Medium: return 'Medium';
      case ChangePriority.High: return 'High';
      case ChangePriority.Critical: return 'Critical';
      default: return 'Unknown';
    }
  }

  getPriorityClass(priority: ChangePriority): string {
    switch (priority) {
      case ChangePriority.Low: return 'low';
      case ChangePriority.Medium: return 'medium';
      case ChangePriority.High: return 'high';
      case ChangePriority.Critical: return 'critical';
      default: return '';
    }
  }

  getWorkflowStateLabel(state: WorkflowState): string {
    switch (state) {
      case WorkflowState.Pending: return 'Pending';
      case WorkflowState.InProgress: return 'In Progress';
      case WorkflowState.Completed: return 'Completed';
      case WorkflowState.Cancelled: return 'Cancelled';
      case WorkflowState.Failed: return 'Failed';
      default: return 'Unknown';
    }
  }

}
